// Lightweight first-party analytics. Sends events to /api/track.
// In dev, that endpoint is handled by the Vite middleware in
// vite.config.js. In prod, by server/index.js. Either way the
// schema is the same — the receiver appends to events.jsonl.
//
// Events captured:
//   - session  (one per page load: referrer, viewport, ua hint)
//   - view     (project enters viewport ≥50%)
//   - dwell    (per-project time-in-view; flushed on hide / unload)
//   - depth    (max scroll depth in 10% buckets)
//   - link     (outbound clicks)
//
// Privacy: no cookies, no fingerprinting. Session id is per-tab,
// regenerated each load and lives only in memory.

const ENDPOINT = '/api/track';

const session = {
  id: cryptoRandom(),
  startedAt: Date.now(),
  path: location.pathname,
};

const projectTimers = new Map(); // slug -> { enteredAt, total }
let maxDepthBucket = 0;
let queue = [];
let flushScheduled = false;

export function initTracking() {
  send({ kind: 'session', ...sessionPayload() });

  // Outbound links
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href) return;
    const isExternal = /^https?:\/\//i.test(href) && !href.includes(location.host);
    if (isExternal) send({ kind: 'link', href });
  }, { passive: true });

  // Flush on tab hide / unload — sendBeacon survives the navigation.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushAll(true);
  });
  window.addEventListener('pagehide', () => flushAll(true));
}

export function trackProjectVisibility(slug, visible) {
  const now = Date.now();
  let entry = projectTimers.get(slug);
  if (visible) {
    if (!entry) entry = { enteredAt: 0, total: 0, viewed: false };
    if (!entry.enteredAt) entry.enteredAt = now;
    if (!entry.viewed) {
      entry.viewed = true;
      send({ kind: 'view', slug });
    }
    projectTimers.set(slug, entry);
  } else if (entry && entry.enteredAt) {
    entry.total += now - entry.enteredAt;
    entry.enteredAt = 0;
    projectTimers.set(slug, entry);
  }
}

export function trackScrollDepth(progress) {
  const bucket = Math.min(10, Math.floor(progress * 10)); // 0..10
  if (bucket > maxDepthBucket) {
    maxDepthBucket = bucket;
    send({ kind: 'depth', pct: bucket * 10 });
  }
}

function flushAll(immediate = false) {
  const now = Date.now();
  for (const [slug, entry] of projectTimers) {
    if (entry.enteredAt) {
      entry.total += now - entry.enteredAt;
      entry.enteredAt = now;
    }
    if (entry.total >= 1000) {
      queue.push({ kind: 'dwell', slug, ms: Math.round(entry.total) });
      entry.total = 0;
    }
  }
  flush(immediate);
}

function send(event) {
  queue.push(event);
  scheduleFlush();
}

function scheduleFlush() {
  if (flushScheduled) return;
  flushScheduled = true;
  setTimeout(() => {
    flushScheduled = false;
    flush(false);
  }, 1500);
}

function flush(useBeacon) {
  if (queue.length === 0) return;
  const batch = {
    sid: session.id,
    t: Date.now(),
    path: session.path,
    events: queue,
  };
  queue = [];
  const body = JSON.stringify(batch);
  try {
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
    } else {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {}
}

function sessionPayload() {
  return {
    ref: document.referrer || null,
    vw: window.innerWidth,
    vh: window.innerHeight,
    dpr: window.devicePixelRatio || 1,
    lang: navigator.language || null,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

function cryptoRandom() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'sid-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Periodic dwell flush so long-stayers still register if the tab
// is closed mid-session via hard kill (no visibilitychange fires).
setInterval(() => flushAll(false), 15000);
